import { getMapboxFilters } from '@/utils/layers/styleFactory/filters'
import { getMapboxColorExpression } from '@/utils/layers/styleFactory/getMapboxColorExpression'
import linesLayers from '@/utils/layers/styleFactory/linesLayers'
import { getMapboxOpacityExpression } from '@/utils/layers/styleFactory/opacity'
import parcelSelectLayer from '@/utils/layers/styleFactory/parcelSelectLayer'
import pointsLayers from '@/utils/layers/styleFactory/pointsLayers'
import polygonsLayers from '@/utils/layers/styleFactory/polygonsLayers'
import polygonsSymbolizedLayers from '@/utils/layers/styleFactory/polygonsSymbolizedLayers'
import rasterLayers from '@/utils/layers/styleFactory/rasterLayers'
import rasterParticleLayers from '@/utils/layers/styleFactory/rasterParticleLayers'
import {
  IBaseDataLayer,
  IDataLayer,
  ILayerValue,
  SimpleLayerSpecification,
  TDataLayerStyle
} from '@lifk/types'
import { groupMulti, isNumber } from '@lifk/utils'
import { ExpressionSpecification } from 'mapbox-gl'

interface Template {
  initMapLayers: (
    layerValues: ILayerValue[],
    mapboxColorExpression: string | ExpressionSpecification,
    mapboxOpacityExpression?: ExpressionSpecification | number
  ) => PartialLayerSpec[]
  // A list of layerIds which should respond to user events such as hover, click, tap, etc.
  interactiveMapLayers: string[]
}

export interface PartialLayerSpec
  extends Omit<SimpleLayerSpecification, 'source'> {
  source?: undefined
}

export interface LegendComponentProps {
  dataLayer: IDataLayer
  layerValues: ILayerValue[]
  hiddenLayerValues: string[]
}

const templates: Record<TDataLayerStyle | 'ParcelSelect', Template> = {
  Polygons: {
    initMapLayers: polygonsLayers,
    interactiveMapLayers: ['fill']
  },
  'Polygons (symbolized)': {
    initMapLayers: polygonsSymbolizedLayers,
    interactiveMapLayers: ['fill']
  },
  Lines: {
    initMapLayers: linesLayers,
    interactiveMapLayers: ['lines-clickable']
  },
  Points: {
    initMapLayers: pointsLayers,
    interactiveMapLayers: ['points']
  },
  Animated: {
    initMapLayers: linesLayers, // not technically needed, but keeps the chain below clean
    interactiveMapLayers: []
  },
  Raster: {
    initMapLayers: rasterLayers,
    interactiveMapLayers: ['raster']
  },
  ParcelSelect: {
    initMapLayers: parcelSelectLayer,
    interactiveMapLayers: ['fill']
  },
  'Animated (particles)': {
    initMapLayers: rasterParticleLayers,
    interactiveMapLayers: ['particle']
  }
}

const getConditionalMinzoom = (minzoom: number | undefined) =>
  isNumber(minzoom) ? { minzoom } : {}
const getConditionalMaxzoom = (maxzoom: number | undefined) =>
  isNumber(maxzoom) ? { maxzoom } : {}

// Given a Data Layer and list of Layer Values that share a single tileset+layer,
// Return mapbox layers with all filters and styling applied
export default function generateTemplate(
  dataLayer: Omit<IBaseDataLayer, 'mapLayers'>,
  layerValues: ILayerValue[],
  templateStyleOverride?: keyof typeof templates
) {
  // assume all layer values with the same tileset+layer+zooms have some shared fields
  const instanceId = layerValues[0].key
  const tilesetUrl = layerValues[0].tilesetUrl
  const tilesetLayer = layerValues[0].tilesetLayer
  const style = layerValues[0].style
  const exclude = layerValues[0].styleConfig.exclude
  const interactive = layerValues[0].interactive
  const layerMinzoom = getConditionalMinzoom(layerValues[0].layerConfig.minzoom)
  const labelMinzoom = getConditionalMinzoom(layerValues[0].labelConfig.minzoom)
  const maxzoom = getConditionalMaxzoom(layerValues[0].layerConfig.maxzoom)

  const template = templateStyleOverride
    ? templates[templateStyleOverride]
    : templates[style]

  // Collect all unique filters for this tileset+layer
  const uniqueLayerFilters = groupMulti(
    layerValues.map(({ layerConfig }) => layerConfig),
    (layerConfig) => [
      layerConfig.filter ? JSON.stringify(layerConfig.filter) : 'none'
    ]
  )
  const uniqueLabelFilters = groupMulti(
    layerValues.map(({ labelConfig }) => labelConfig),
    (labelConfig) => [
      labelConfig.filter ? JSON.stringify(labelConfig.filter) : 'none'
    ]
  )
  const layerFilters = getMapboxFilters(uniqueLayerFilters)
  const labelFilters = getMapboxFilters(uniqueLabelFilters)

  // Mapbox color(s) (style by filter if applicable)
  const colorOpacityConfig = layerValues.map(
    ({ styleConfig, layerConfig, style }) => ({
      filter: layerConfig.filter,
      color: styleConfig.color,
      opacity: styleConfig.opacity,
      style,
      minValue: styleConfig.minValue,
      maxValue: styleConfig.maxValue,
      targetAttribute: styleConfig.targetAttribute
    })
  )
  // if >1 unique filter, use filters to determine coloring
  const colorByFilter = Object.keys(uniqueLayerFilters).length > 1
  const mapboxColorExpression = getMapboxColorExpression(
    colorOpacityConfig,
    colorByFilter,
    dataLayer.isGradient
  )
  const mapboxOpacityExpression = getMapboxOpacityExpression(
    colorOpacityConfig,
    colorByFilter,
    dataLayer.isGradient
  )

  const mapLayers: SimpleLayerSpecification[] = template
    // Apply styles to template layers
    .initMapLayers(layerValues, mapboxColorExpression, mapboxOpacityExpression)
    // Exclude specified layers, except for 'fill'.
    //   Excluded 'fill' layers still render as transparent so they can capture click events.
    .filter(
      (layer) =>
        !exclude.some((exclude) => exclude === layer.id && exclude !== 'fill')
    )
    .map((layer) => {
      const isLabel = layer.id === 'label'

      return {
        // Starting with the current layer so far
        ...layer,
        // Add label or layer minzoom and filtering if applicable
        ...(isLabel ? labelMinzoom : layerMinzoom),
        ...(isLabel ? labelFilters : layerFilters),
        ...maxzoom,
        // Create a unique ID by concatenating the IDs of the layer, instance, and template
        id: [dataLayer.key, instanceId, layer.id].join('.'),
        source: tilesetUrl,
        // "label" is a magic layer ID that templates may provide to
        // automatically label features in the provided layer. Features
        // may correspond to one or more points in another layer identified
        // by a matching id suffixed as below
        'source-layer': isLabel ? tilesetLayer + '-label' : tilesetLayer,
        // Use metadata to pass forward interactivity configuration
        metadata: {
          interactive: interactive
            ? template.interactiveMapLayers?.includes(layer.id)
            : false
        }
      } as SimpleLayerSpecification
    })

  return mapLayers
}
